import {
  Badge,
  Card,
  CardContent,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@blog/ui";

import { t } from "../lib/i18n";
import { useApi } from "../app/ApiContext";
import { PageHeader } from "../app/Shell";
import { formatDateTime } from "../lib/format";
import { useAsync, useSite } from "../lib/hooks";

/**
 * Deployments (TASK-708, SPEC §56).
 *
 * Read-only: a deployment is recorded by whatever runs the build, and the CMS
 * reports it. A "deploy" button here would mean the Admin held a credential
 * that can publish the site — a much larger surface than reading a status row.
 */
export function Deployments() {
  const client = useApi();
  const { site, loading: siteLoading } = useSite(client);
  const siteId = site?.id;

  const data = useAsync(async () => {
    if (siteId === undefined) {
      return null;
    }

    return client.listDeployments(siteId);
  }, [siteId]);

  if (siteLoading || data.loading) {
    return <p className="text-sm text-neutral-500">{t("common.loading")}</p>;
  }

  if (data.error !== null) {
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-red-600">{data.error}</p>
        </CardContent>
      </Card>
    );
  }

  const deployments = data.data ?? [];

  return (
    <>
      <PageHeader
        title={t("deployments.title")}
        description={t("deployments.countDesc", { count: deployments.length })}
      />

      {deployments.length === 0 ? (
        <EmptyState
          message={t("deployments.none")}
          hint={t("deployments.emptyHint")}
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead>{t("deployments.thCommit")}</TableHead>
                <TableHead>{t("deployments.thStarted")}</TableHead>
                <TableHead>{t("deployments.thUrl")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deployments.map((deployment) => (
                <TableRow key={deployment.id}>
                  <TableCell>
                    <Badge
                      variant={
                        deployment.status === "success"
                          ? "success"
                          : deployment.status === "failed"
                            ? "destructive"
                            : deployment.status === "cancelled"
                              ? "outline"
                              : "warning"
                      }
                    >
                      {deployment.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {deployment.commitSha?.slice(0, 7) ?? "—"}
                  </TableCell>
                  <TableCell className="text-neutral-500">
                    {formatDateTime(deployment.createdAt)}
                  </TableCell>
                  <TableCell className="text-xs">
                    {deployment.url === undefined ? (
                      "—"
                    ) : (
                      <a className="text-neutral-700 hover:underline" href={deployment.url}>
                        {deployment.url}
                      </a>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </>
  );
}
